import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SubAccount } from '@tradingblock/types';
import { useDispatcher } from '../../data/global/hooks';
import { AccountActions } from '../../data/global/actions/AccountActions';
import { Actions, DataActions } from '../../data/global/actions';
import { account } from '../../data/global/selectors/accountSelectors';
import { formatAccountNumber, isSelectedSubAccount } from '../../utilities/account';
import styled from 'styled-components';

export const SelectSubAccountButton: React.FC<{ subAccount: SubAccount; type: 'link' | 'btn'; disabled?: boolean }> = ({
  subAccount,
  type,
  disabled,
}) => {
  const { dispatch } = useDispatcher();

  const accountId = useSelector(account.accountId);
  const accountNumber = useSelector(account.accountNumber);
  const selectedSubAccountId = useSelector(account.subAccountId);

  const selected = useMemo(() => isSelectedSubAccount(selectedSubAccountId, subAccount), [selectedSubAccountId]);

  const onSelectSubAccount = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (selected) return; // Prevent duplicate clicks
      const subaccountId = subAccount.Id === -1 ? undefined : subAccount.Id;
      dispatch(AccountActions.setSubAccount({ subaccountId }));
      dispatch(AccountActions.toggleAccountSwitcher({ show: false }));
      dispatch(Actions.requestAccountBalances({ throttle: false }));
      dispatch(DataActions.requestPositions({ accountId, subaccountId, more: true }, { debounce: 1000 }));

      if (accountId) {
        dispatch(Actions.requestAccountOrders({ accountId, subaccountId }, { debounce: 2000 }));
      }
    },
    [selectedSubAccountId, dispatch, accountId, subAccount.Id]
  );

  if (type === 'link') {
    return (
      <button type="button" className="btn-link" onClick={onSelectSubAccount} disabled={disabled}>
        {formatAccountNumber(accountNumber, subAccount.AcctSuffix)}
      </button>
    );
  }

  if (selected) {
    return (
      <SelectedBadge>
        <i className="fa fa-check" /> Selected
      </SelectedBadge>
    );
  }

  return (
    <SelectionButton onClick={onSelectSubAccount} disabled={disabled}>
      Select
    </SelectionButton>
  );
};

const SelectionButton = styled.button`
  width: 100%;
  min-width: 80px;
  padding: 15px 20px;
  font-weight: bold;
  text-transform: uppercase;
  border-radius: 100px;
  transition: all 0.2s ease-in-out;
  background: transparent;
  border: 1px solid var(--blue);

  color: var(--blue);
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.5); /* Lighten up border */
  min-height: 40px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: white;
    scale: 1.02;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SelectedBadge = styled.strong`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 80px;
  padding: 15px 20px;
  background: teal;
  color: white;
  border-radius: 100px;
  font-weight: bold;
  text-transform: uppercase;
  min-height: 40px;

  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease-in-out;
  &:hover {
    scale: 1.02;
  }

  /* Remove unnecessary spacing */
  i {
    margin-right: 8px;
  }
`;
